import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCog,
  Layers,
  CalendarDays,
  CalendarCheck,
  BarChart3,
  FileBarChart,
  Wallet,
  Bell,
  MessageSquare,
  LogOut,
  Smartphone,
  StickyNote,
  History,
  Briefcase,
  Boxes,
  Droplets,
  Palette,
  ClipboardList,
  FileText,
  ReceiptText,
  CalendarPlus,
  Building2,
  DollarSign,
  Menu,
  X,
  Activity,
  Clock,
  Inbox,
  UsersRound,
  MapPinned,
  HelpCircle,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { DemoTour } from "./DemoTour";
import { useI18n } from "../i18n/I18nContext";
import { useBranding } from "../branding/BrandingContext";
import { usePlatformPortalTrigger } from "../hooks/usePlatformPortalTrigger";
import { api } from "../lib/api";
import { onSidebarCountsRefresh } from "../lib/sidebarBus";
import { ReleaseNotesAnnouncer } from "./ReleaseNotesAnnouncer";
import { TrialBadge } from "./TrialBadge";

const itemBase =
  "flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors min-h-[44px]";

// Cores suaves e distintas por menu (aplicadas ao ícone quando não-ativo).
const MENU_COLORS = {
  dashboard: "text-sky-500",
  today: "text-cyan-500",
  schedule: "text-indigo-500",
  "schedule-release": "text-violet-500",
  clients: "text-blue-500",
  collaborators: "text-emerald-500",
  teams: "text-amber-500",
  positions: "text-yellow-600",
  "booking-requests": "text-rose-500",
  finance: "text-teal-600",
  receivables: "text-green-600",
  invoices: "text-cyan-600",
  payments: "text-lime-600",
  estimates: "text-orange-500",
  supplies: "text-fuchsia-500",
  equipments: "text-blue-600",
  reports: "text-purple-500",
  history: "text-slate-500",
  notes: "text-amber-600",
  notifications: "text-pink-500",
  "notification-history": "text-pink-400",
  "notification-settings": "text-pink-600",
  "audit-log": "text-rose-600",
  "company-settings": "text-slate-600",
  "status-colors": "text-indigo-400",
  billing: "text-emerald-600",
};


export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const { branding } = useBranding();
  const portalTrigger = usePlatformPortalTrigger();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [counts, setCounts] = useState(null);

  // Carrega contadores ao montar e revalida ao mudar de rota.
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = () => {
      api
        .get("/sidebar-counts")
        .then(({ data }) => {
          if (!cancelled) setCounts(data);
        })
        .catch(() => {});
    };
    fetchCounts();
    // Revalida também quando algum CRUD dispara o evento global.
    const off = onSidebarCountsRefresh(fetchCounts);
    return () => {
      cancelled = true;
      off();
    };
  }, [loc.pathname]);

  // Fecha o drawer ao mudar de rota
  useEffect(() => {
    setDrawerOpen(false);
  }, [loc.pathname]);

  // Trava o scroll do body quando o drawer está aberto
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  // disabled_menus comes from the user (enriched at /auth/me) and is
  // tenant-scoped. Super-admins always ignore the list.
  const disabledMenus = new Set(user?.disabled_menus || []);
  const isMenuBlocked = (key) =>
    key && !user?.is_super_admin && disabledMenus.has(key);

  const NavItem = ({ to, icon: Icon, label, testId, end, badge, subtleBadge, menuKey }) => {
    if (isMenuBlocked(menuKey)) return null;
    const iconColor = MENU_COLORS[menuKey] || "text-slate-500";
    return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `${itemBase} ${isActive ? "nav-item-active" : ""}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={18}
            strokeWidth={2}
            className={isActive ? "" : iconColor}
          />
          <span className="flex-1 truncate">{label}</span>
          {badge != null && badge !== "" && (
            <span
              className="text-[10px] font-bold px-1.5 min-w-[20px] h-5 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center"
              data-testid={`${testId}-badge`}
            >
              {badge}
            </span>
          )}
          {subtleBadge != null && subtleBadge !== "" && (
            <span
              className={`text-[10px] font-bold px-1.5 min-w-[20px] h-5 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center ${
                Number(subtleBadge) >= 5 ? "badge-pulse" : ""
              }`}
              data-testid={`${testId}-overdue`}
              title={`${subtleBadge} ${t("itens requerendo atenção")}`}
            >
              {subtleBadge}
            </span>
          )}
        </>
      )}
    </NavLink>
    );
  };

  const SidebarContent = ({ variant = "desktop" }) => (
    <>
      <div className="flex items-center justify-between gap-2 px-2 pb-6">
        <div
          className="flex items-center gap-2 min-w-0 cursor-default select-none"
          onClick={portalTrigger}
          data-testid={`sidebar-logo-trigger-${variant}`}
        >
          {branding.logo_path ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${branding.logo_path}`}
              alt={branding.name}
              className="w-10 h-10 rounded-xl object-contain bg-white flex-shrink-0"
              draggable={false}
              onError={(e) => {
                e.currentTarget.src = "/assets/cleanmanager-emblem.png";
              }}
            />
          ) : (
            <img
              src="/assets/cleanmanager-emblem.png"
              alt={branding.name}
              className="w-10 h-10 rounded-xl object-contain bg-white flex-shrink-0"
              draggable={false}
            />
          )}
          <div className="min-w-0">
            <div
              className="font-bold text-lg leading-tight truncate"
              data-testid="sidebar-company-name"
            >
              {branding.name}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">
              {branding.app_subtitle}
            </div>
          </div>
        </div>
        {/* Botão fechar — apenas no drawer mobile */}
        {variant === "mobile" && (
          <button
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
            data-testid="sidebar-close-btn"
            aria-label={t("Fechar menu")}
          >
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto">
        <NavItem to="/dashboard" end icon={LayoutDashboard} label="Dashboard" testId={`nav-dashboard-${variant}`} menuKey="dashboard" />
        <NavItem
          to="/hoje"
          icon={Activity}
          label={t("Resumo do dia")}
          testId={`nav-today-${variant}`}
          menuKey="today"
        />
        <NavItem
          to="/schedule"
          icon={CalendarDays}
          label={t("Agenda")}
          testId={`nav-schedule-${variant}`}
          badge={counts?.schedule_today || null}
          menuKey="schedule"
        />
        {user?.role === "admin" && (
          <NavItem
            to="/schedule-release"
            icon={Clock}
            label={t("Liberação da agenda")}
            testId={`nav-schedule-release-${variant}`}
            menuKey="schedule-release"
          />
        )}
        {user?.role === "admin" && (
          <NavItem
            to="/agenda/corrigir"
            icon={CalendarCheck}
            label={t("Corrigir agenda")}
            testId={`nav-schedule-audit-${variant}`}
            menuKey="schedule"
            subtleBadge={counts?.schedule_duplicates || null}
          />
        )}

        <div className="nav-group-label">{t("Cadastros")}</div>
        <NavItem
          to="/clients"
          icon={Users}
          label={t("Clientes")}
          testId={`nav-clients-${variant}`}
          badge={counts?.clients_total || null}
          subtleBadge={counts?.clients_overdue || null}
          menuKey="clients"
        />
        <NavItem
          to="/booking-requests"
          icon={CalendarPlus}
          label={t("Solicitações")}
          testId={`nav-booking-requests-${variant}`}
          subtleBadge={counts?.pending_requests || null}
          menuKey="booking-requests"
        />

        <div className="nav-group-label">{t("Equipe")}</div>
        <NavItem
          to="/collaborators"
          icon={UserCog}
          label={t("Colaboradores")}
          testId={`nav-collaborators-${variant}`}
          badge={counts?.collaborators_total || null}
          subtleBadge={
            (Number(counts?.pending_invitations) || 0) +
              (Number(counts?.collaborators_pending_activation) || 0) || null
          }
          menuKey="collaborators"
        />
        <NavItem
          to="/teams"
          icon={Layers}
          label={t("Equipes")}
          testId={`nav-teams-${variant}`}
          badge={counts?.teams_total || null}
          menuKey="teams"
        />
        <NavItem to="/positions" icon={Briefcase} label={t("Cargos")} testId={`nav-positions-${variant}`} menuKey="positions" />
        {(user?.role === "admin" || user?.role === "supervisor") && (
          <NavItem to="/banco-colaboradores" icon={UsersRound} label="Banco de Colaboradores" testId={`nav-talent-bank-${variant}`} subtleBadge={counts?.talent_new || null} />
        )}

        <div className="nav-group-label">{t("Estoque")}</div>
        <NavItem to="/equipments" icon={Boxes} label={t("Equipamentos")} testId={`nav-equipments-${variant}`} menuKey="equipments" />
        <NavItem to="/supplies" icon={Droplets} label={t("Itens de limpeza")} testId={`nav-supplies-${variant}`} menuKey="supplies" subtleBadge={counts?.supply_alerts_open || null} />

        <div className="nav-group-label">{t("Financeiro")}</div>
        {user?.role === "admin" && (
          <NavItem to="/estimates" icon={ClipboardList} label="Invoice" testId={`nav-estimates-${variant}`} menuKey="estimates" />
        )}
        {user?.role === "admin" && (
          <NavItem to="/faturas" icon={ReceiptText} label="New Invoice" testId={`nav-custom-invoices-${variant}`} menuKey="estimates" />
        )}
        <NavItem to="/finance" end icon={Wallet} label={t("Pagamentos")} testId={`nav-finance-${variant}`} menuKey="finance" />
        <NavItem to="/recebimentos" icon={DollarSign} label={t("Recebimentos")} testId={`nav-receivables-${variant}`} menuKey="receivables" />
        {user?.role === "admin" && (
          <NavItem to="/a-receber" icon={FileText} label={t("A Receber")} testId={`nav-invoices-${variant}`} menuKey="invoices" />
        )}
        <NavItem to="/finance/reports" icon={FileBarChart} label={t("Relatórios financeiros")} testId={`nav-finance-reports-${variant}`} menuKey="finance" />
        {user?.role === "admin" && (
          <NavItem to="/finance/payments" icon={Wallet} label={t("Histórico de pagamentos")} testId={`nav-payments-history-${variant}`} menuKey="payments" />
        )}
        <NavItem to="/reports" icon={BarChart3} label={t("Relatórios gerais")} testId={`nav-reports-${variant}`} menuKey="reports" />
        <NavItem to="/historico" icon={History} label={t("Histórico")} testId={`nav-history-${variant}`} menuKey="history" />

        {user?.role === "admin" && (
          <>
            <div className="nav-group-label">{t("Pessoal")}</div>
            <NavItem to="/notas" icon={StickyNote} label={t("Minhas notas")} testId={`nav-notes-${variant}`} menuKey="notes" />
          </>
        )}

        <div className="nav-group-label">{t("Comunicação")}</div>
        <NavItem to="/notifications" icon={Bell} label={t("Notificações")} testId={`nav-notifications-${variant}`} menuKey="notifications" />
        <NavItem to="/notification-settings" icon={MessageSquare} label={t("Templates de mensagem")} testId={`nav-notif-settings-${variant}`} menuKey="notification-settings" />
        <NavItem to="/notification-history" icon={History} label={t("Histórico de envios")} testId={`nav-notif-history-${variant}`} menuKey="notification-history" />

        <div className="nav-group-label">{t("Configurações")}</div>
        {user?.role === "admin" && (
          <NavItem to="/configuracoes/empresa" icon={Building2} label={t("Dados da Empresa")} testId={`nav-company-settings-${variant}`} menuKey="company-settings" />
        )}
        <NavItem to="/cores" icon={Palette} label={t("Cores")} testId={`nav-status-colors-${variant}`} menuKey="status-colors" />
        {user?.role === "admin" && (
          <NavItem to="/audit-log" icon={History} label="Audit Log" testId={`nav-audit-log-${variant}`} menuKey="audit-log" />
        )}
        {user?.role === "admin" && (
          <NavItem to="/billing" icon={DollarSign} label={t("Plano & Cobrança")} testId={`nav-billing-${variant}`} menuKey="billing" />
        )}
      </nav>

      <div className="border-t border-slate-200 pt-4 mt-4">
        {user?.is_super_admin ? (
          <div
            className="block px-2 mb-3 p-2 -ml-2"
            data-testid={`nav-profile-${variant}`}
          >
            <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
              {user?.name}
              {user?.auth_provider === "google" && <GoogleBadge />}
            </div>
            <div className="text-xs text-violet-600 font-semibold">
              Super Admin
            </div>
          </div>
        ) : (
          <Link
            to="/perfil"
            data-testid={`nav-profile-${variant}`}
            className="block px-2 mb-3 hover:bg-slate-50 rounded-lg p-2 -ml-2 transition-colors"
          >
            <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
              <span className="truncate">{user?.name}</span>
              {user?.auth_provider === "google" && <GoogleBadge />}
            </div>
            <div className="text-xs text-blue-600 group-hover:underline">{t("Editar perfil →")}</div>
          </Link>
        )}
        <button
          type="button"
          data-testid={`install-app-btn-${variant}`}
          onClick={() => {
            window.dispatchEvent(new CustomEvent("cm-open-install-prompt"));
          }}
          className={`${itemBase} w-full justify-start text-blue-700 hover:bg-blue-50`}
        >
          <Smartphone size={18} /> {t("Instalar app")}
        </button>
        <button
          data-testid={`logout-btn-${variant}-sidebar`}
          onClick={async () => {
            await logout();
            nav("/login");
          }}
          className={`${itemBase} w-full justify-start`}
        >
          <LogOut size={18} /> {t("Sair")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--cm-bg)] flex" data-testid="admin-shell">
      {/* Sidebar fixa (desktop) */}
      <aside
        className="hidden lg:flex flex-col w-64 border-r border-slate-200 bg-white px-4 py-5"
        data-testid="admin-sidebar"
      >
        <SidebarContent variant="desktop" />
      </aside>

      {/* Drawer mobile + overlay */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-overlay"
          aria-hidden="true"
        />
      )}
      <aside
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-[85%] max-w-[320px] bg-white z-50 flex-col px-4 py-5 shadow-2xl transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0 flex" : "-translate-x-full flex"
        }`}
        data-testid="admin-sidebar-mobile"
        aria-hidden={!drawerOpen}
      >
        <SidebarContent variant="mobile" />
      </aside>

      {/* Top bar mobile com hamburger */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-20 bg-white border-b border-slate-200 px-3 py-2 flex items-center justify-between gap-2">
        <button
          data-testid="sidebar-open-btn"
          onClick={() => setDrawerOpen(true)}
          className="p-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={t("Abrir menu")}
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {branding.logo_path ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${branding.logo_path}`}
              alt={branding.name}
              className="w-10 h-10 rounded-lg object-contain flex-shrink-0 bg-white"
              onError={(e) => {
                e.currentTarget.src = "/assets/cleanmanager-emblem.png";
              }}
            />
          ) : (
            <img
              src="/assets/cleanmanager-emblem.png"
              alt={branding.name}
              className="w-10 h-10 rounded-lg object-contain flex-shrink-0 bg-white"
            />
          )}
          <div className="font-bold truncate text-sm sm:text-base">{branding.name}</div>
        </div>
        {!isMenuBlocked("booking-requests") && (
          <button
            data-testid="pending-requests-btn-mobile"
            onClick={() => nav("/booking-requests")}
            className="relative p-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600"
            aria-label={t("Solicitações pendentes")}
            title={t("Solicitações pendentes")}
          >
            <Inbox size={20} />
            {Number(counts?.pending_requests) > 0 && (
              <span
                data-testid="pending-requests-badge-mobile"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center"
              >
                {counts.pending_requests}
              </span>
            )}
          </button>
        )}
        <button
          data-testid="logout-btn-mobile"
          onClick={async () => {
            await logout();
            nav("/login");
          }}
          className="p-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600"
          aria-label={t("Sair")}
        >
          <LogOut size={18} />
        </button>
      </div>

      <main className="flex-1 lg:ml-0 p-3 sm:p-4 lg:p-8 pt-16 sm:pt-20 lg:pt-8 pb-20 lg:pb-8 max-w-[1500px] mx-auto w-full overflow-x-hidden">
        {user?.is_demo && (
          <div data-testid="demo-banner" className="mb-4 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              <strong>🔒 Modo demonstração</strong> — você está explorando o CleanManager com dados de exemplo. Alterações não são salvas (somente leitura).
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => window.dispatchEvent(new Event("cm:demo-tour"))} data-testid="demo-tour-restart"
                title="Ver o tour guiado" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border text-amber-800 text-sm font-semibold hover:bg-amber-50">
                <HelpCircle size={15} /> Ver tour
              </button>
              <a href="/signup" data-testid="demo-signup" className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">Criar minha conta</a>
              <button onClick={async () => { await logout(); window.location.href = "/"; }} data-testid="demo-exit"
                className="px-3 py-1.5 rounded-lg bg-white border text-slate-700 text-sm font-semibold hover:bg-slate-50">Sair da demo</button>
            </div>
          </div>
        )}
        <TrialBadge />
        <div className="fade-in">{children}</div>
        <ReleaseNotesAnnouncer />
        <DemoTour active={!!user?.is_demo} onboarding={user?.role === "admin" && !user?.is_demo} />

        {/* Bottom nav mobile (atalhos rápidos) */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 grid grid-cols-5 pb-safe z-10">
          {[
            { to: "/dashboard", icon: LayoutDashboard, label: lang === "en" ? "Home" : "Início" },
            { to: "/schedule", icon: CalendarDays, label: t("Agenda") },
            { to: "/clients", icon: Users, label: t("Clientes") },
            { to: "/collaborators", icon: UserCog, label: t("Equipe") },
            { to: "/reports", icon: BarChart3, label: t("Relatórios") },
          ].map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[11px] min-h-[56px] ${
                  isActive ? "text-blue-700" : "text-slate-500"
                }`
              }
            >
              <it.icon size={20} />
              <span className="mt-0.5">{it.label}</span>
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}

function GoogleBadge() {
  const { t } = useI18n();
  return (
    <span
      title={t("Conta conectada com Google")}
      data-testid="google-linked-badge"
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white border border-slate-200 shadow-sm flex-shrink-0"
    >
      <svg width="10" height="10" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
      </svg>
    </span>
  );
}

